import React from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  DEFAULT_SEARCH,
  rangeLengthDays,
  resolveRange,
  toFilterInput,
  type AnalyticsSearch,
  type FilterInput,
  type Grain,
} from './filters';
import { comparisonInput } from './compare';

type AnalyticsContextValue = {
  search: AnalyticsSearch;
  /** The exact input every report query takes (stable per URL state). */
  input: FilterInput;
  grain: Grain;
  rangeDays: number;
  /** Same filters over the comparison window, or null when not comparing. */
  compareInput: FilterInput | null;
  update: (patch: Partial<AnalyticsSearch>) => void;
};

const AnalyticsContext = React.createContext<AnalyticsContextValue | null>(null);

/** Drops defaults and empty values so links stay short. */
export function compactSearch(search: AnalyticsSearch): Partial<AnalyticsSearch> {
  const out: Partial<AnalyticsSearch> = {};
  (Object.keys(search) as (keyof AnalyticsSearch)[]).forEach((key) => {
    const value = search[key];
    if (value === undefined || value === '' || value === false) return;
    if (Array.isArray(value) && value.length === 0) return;
    if (key in DEFAULT_SEARCH && DEFAULT_SEARCH[key as keyof typeof DEFAULT_SEARCH] === value) return;
    (out as Record<string, unknown>)[key] = value;
  });
  return out;
}

export function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  const search = useSearch({ from: '/analytics' }) as AnalyticsSearch;
  const navigate = useNavigate({ from: '/analytics' });

  const value = React.useMemo<AnalyticsContextValue>(() => {
    const now = new Date();
    const input = toFilterInput(search, now);
    return {
      search,
      input,
      grain: input.grain,
      rangeDays: rangeLengthDays(resolveRange(search, now)),
      compareInput: comparisonInput(search, input, now),
      update: (patch) => {
        const next = compactSearch({ ...search, ...patch });
        // Custom range needs both bounds; anything else drops them.
        if (next.range !== 'custom') {
          delete next.from;
          delete next.to;
        }
        void navigate({ search: next as never, replace: true });
      },
    };
    // The URL state is the only input; "now" is sampled once per change on purpose.
  }, [search, navigate]);

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
}

export function useAnalytics(): AnalyticsContextValue {
  const ctx = React.useContext(AnalyticsContext);
  if (!ctx) throw new Error('useAnalytics must be used inside AnalyticsProvider');
  return ctx;
}
